import { useEffect, useState } from "react";
import { http } from "../api";
import { Newspaper, Sparkles, Loader, Mail } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

export default function WeeklyDigest() {
  const [inbox, setInbox] = useState([]);
  const [active, setActive] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const r = await http.get("/digest/inbox");
    setInbox(r.data);
    if (r.data.length && !active) setActive(r.data[0]);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true); setError("");
    try {
      const r = await http.post("/digest/generate");
      setActive(r.data);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setGenerating(false); }
  };

  return (
    <>
      <PageHeader eyebrow="EVERY MONDAY" title="AI weekly digest"
        subtitle="Bilingual (English + Bengali) recap of complaints, turnout, donations and top volunteers"
        actions={<button className="primary" data-testid="generate-digest-button" disabled={generating} onClick={generate}>
          {generating ? <><Loader size={15} className="spin" /> Generating…</> : <><Sparkles size={15} /> Generate now</>}
        </button>} />

      <div className="notice-strip" data-testid="digest-notice">
        <Mail size={15} />
        <span>Email delivery is <b>in-app inbox only</b> until a provider (Resend / SendGrid) is configured. Every digest is a permanent record here.</span>
      </div>

      {error && <div className="notice-strip" style={{ background: "#fae7e4", color: "#b04a3f", borderColor: "#efc0b7" }}>{error}</div>}

      <div className="digest-grid">
        <aside className="panel digest-inbox">
          <div className="panel-head" style={{ padding: "18px 18px 12px" }}><h2>Inbox</h2><p>{inbox.length} digests</p></div>
          {inbox.length === 0 ? <EmptyState icon={Newspaper} title="No digests" hint="Click 'Generate now' to create the first weekly recap." /> : (
            inbox.map((d, i) => (
              <button key={d.digest_id} className={`digest-row ${active?.digest_id === d.digest_id ? "active" : ""}`}
                data-testid={`digest-row-${i}`} onClick={() => setActive(d)}>
                <b>{d.headline_en}</b>
                <small>{d.week_of} · {d.provider}</small>
              </button>
            ))
          )}
        </aside>

        <section className="panel digest-body">
          {!active ? <EmptyState icon={Newspaper} title="Generate a digest" hint="Your first weekly recap will appear here." /> : (
            <>
              <div className="digest-head">
                <p className="eyebrow">WEEK OF {active.week_of}</p>
                <h2>{active.headline_en}</h2>
                <p className="digest-bn-headline">{active.headline_bn}</p>
                <small className="hint">Generated by {active.provider} · {new Date(active.created_at).toLocaleString()}</small>
              </div>

              <div className="digest-stats">
                {[
                  { label: "Complaints resolved", value: `${active.stats.resolved}/${active.stats.new_complaints}` },
                  { label: "Turnout coverage", value: `${active.stats.turnout_pct}%` },
                  { label: "Donations", value: `₹${Number(active.stats.total_donations || 0).toLocaleString("en-IN")}` },
                  { label: "Active volunteers", value: active.stats.active_volunteers },
                ].map((s) => (
                  <div key={s.label} className="digest-stat" data-testid={`digest-stat-${s.label.toLowerCase().replaceAll(" ", "-")}`}>
                    <b>{s.value}</b><span>{s.label}</span>
                  </div>
                ))}
              </div>

              <div className="digest-section" data-testid="digest-english">
                <p className="eyebrow">ENGLISH</p>
                <p className="digest-para">{active.summary_en}</p>
                <div className="digest-insight"><Sparkles size={14} /> <span>{active.insight_en}</span></div>
              </div>

              <div className="digest-section" data-testid="digest-bengali">
                <p className="eyebrow">বাংলা</p>
                <p className="digest-para">{active.summary_bn}</p>
                <div className="digest-insight"><Sparkles size={14} /> <span>{active.insight_bn}</span></div>
              </div>

              <div className="digest-section">
                <p className="eyebrow">TOP VOLUNTEERS</p>
                <div className="digest-vols">
                  {active.stats.top_volunteers?.map((v, i) => (
                    <div key={v.name} className="digest-vol">
                      <span className="avatar person-avatar small">{v.name.split(" ").map((x) => x[0]).join("")}</span>
                      <div><b>{v.name}</b><small>#{i + 1} · {v.points} pts</small></div>
                    </div>
                  ))}
                </div>
              </div>

              {active.stats.upcoming_events?.length > 0 && (
                <div className="digest-section">
                  <p className="eyebrow">UPCOMING EVENTS</p>
                  {active.stats.upcoming_events.map((e) => (
                    <div key={e.title} className="digest-event">
                      <b>{e.title}</b><small>{(e.starts_at || "").slice(0, 16).replace("T", " · ")}</small>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
